import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SeoMeta } from "@/components/Jsonld";
import {
  ShieldCheck, Lock, CheckCircle2, ArrowRight, Phone,
  Printer, FileText, Clock,
} from "lucide-react";
import BrandedInvoice from "@/components/BrandedInvoice";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Pay() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const load = useCallback(() => {
    return axios.get(`${API}/api/public/invoices/${id}`)
      .then((r) => setInvoice(r.data))
      .catch((e) => {
        const msg = e.response?.data?.detail || "Could not load invoice";
        toast.error(msg);
        setInvoice({ _error: msg, _code: e.response?.status });
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Stripe redirected back here with ?session_id=...
  // Poll backend until status flips to "paid".
  useEffect(() => {
    const sessionId = sp.get("session_id");
    if (!sessionId) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          clearInterval(t);
          toast.success("Payment confirmed");
          load();
        }
      } catch { /* ignore */ }
      if (tries > 20) clearInterval(t); // ~60s
    }, 3000);
    return () => clearInterval(t);
  }, [sp, load]);

  async function pay() {
    setPaying(true);
    try {
      const r = await axios.post(
        `${API}/api/public/invoices/${id}/checkout`,
        {},
        { headers: { Origin: window.location.origin } },
      );
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  }

  if (loading) {
    return <Shell><p className="text-center text-cream/60 py-20">Loading invoice…</p></Shell>;
  }
  if (invoice?._error) {
    return (
      <Shell>
        <div className="text-center py-16 max-w-md mx-auto">
          <div className="text-orange display-retro text-3xl uppercase tracking-tight">Lost in transit</div>
          <p className="text-cream/70 mt-3">{invoice._error}</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-orange hover:text-cream">
            <ArrowRight className="w-4 h-4 rotate-180" /> Mission control
          </Link>
        </div>
      </Shell>
    );
  }

  const paid = invoice.status === "paid" || sp.get("paid") === "1";
  const cancelled = sp.get("cancelled") === "1";

  if (showInvoice) {
    return <BrandedInvoice invoice={invoice} onBack={() => setShowInvoice(false)} />;
  }

  return (
    <Shell>
      <SeoMeta
        title={`Pay ${invoice.invoice_number} · Space Appliance Repair`}
        description="Secure payment via Stripe — Space Appliance Repair, San Antonio."
      />

      <div className="max-w-md mx-auto pb-12">
        {paid ? <PaidBanner /> : cancelled ? <CancelledBanner /> : null}

        {/* Invoice card */}
        <article className="bg-navy-2/60 border-2 border-orange/40 rounded-xl shadow-[0_0_60px_rgba(230,92,0,0.15)] overflow-hidden">
          <header className="px-6 py-5 border-b border-orange/30 bg-navy">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="label-retro text-[10px] uppercase tracking-widest text-orange">Invoice</div>
                <div className="display-retro text-cream text-xl mt-0.5 num">{invoice.invoice_number}</div>
              </div>
              {paid ? (
                <span className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-400/50 text-emerald-300 text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" /> Paid
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-orange/15 border border-orange/50 text-orange text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1.5 rounded-full">
                  <Clock className="w-3 h-3" /> Due
                </span>
              )}
            </div>
          </header>

          {/* Customer + Appliance */}
          <div className="px-6 py-4 border-b border-cream/10">
            <div className="label-retro text-[10px] uppercase tracking-widest text-cream/50">Billed to</div>
            <div className="text-cream font-medium mt-1">{invoice.customer_name || "—"}</div>
            <div className="text-cream/60 text-sm mt-0.5">{invoice.address || ""}</div>
            {invoice.appliance && (
              <div className="mt-3 bg-navy border border-orange/30 rounded-md px-3 py-2.5">
                <div className="text-orange text-[10px] uppercase tracking-widest font-semibold">Appliance</div>
                <div className="text-cream text-sm font-medium capitalize mt-0.5">{invoice.appliance}</div>
                {(invoice.appliance_make || invoice.appliance_model) && (
                  <div className="text-cream/60 text-xs mt-1 num">
                    {invoice.appliance_make || "—"} · {invoice.appliance_model || "—"}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="px-6 py-4 border-b border-cream/10 space-y-2.5">
            {invoice.line_items.map((li, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-cream">{li.description}</div>
                  <div className="text-cream/40 text-[10px] uppercase tracking-widest mt-0.5 num">
                    {li.qty} × ${Number(li.unit_price).toFixed(2)}{li.kind && li.kind !== "other" ? ` · ${li.kind}` : ""}
                  </div>
                </div>
                <div className="text-cream num font-medium shrink-0">
                  ${(Number(li.qty) * Number(li.unit_price)).toFixed(2)}
                </div>
              </div>
            ))}
            {invoice.diagnostic_fee_waived && (
              <div className="flex items-start justify-between gap-3 text-sm bg-orange/10 border border-orange/30 rounded-md px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-orange italic">Diagnostic fee — <b>waived</b></div>
                  <div className="text-orange/70 text-[10px] uppercase tracking-widest mt-0.5">Courtesy</div>
                </div>
                <div className="text-emerald-400 num font-semibold shrink-0">$0.00</div>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="px-6 py-4 space-y-1.5 text-sm">
            <Row label="Subtotal" value={invoice.subtotal} />
            {Number(invoice.service_fee) > 0 && <Row label="Service fees" value={invoice.service_fee} />}
            {Number(invoice.discount) > 0 && <Row label="Discount" value={-Math.abs(invoice.discount)} tone="emerald" />}
            {invoice.tax_rate > 0 && (
              <Row label={`Tax (${(invoice.tax_rate * 100).toFixed(2)}%)`} value={invoice.tax} />
            )}
            {Number(invoice.card_fee_rate) > 0 && Number(invoice.card_fee) > 0 && (
              <Row label={`Card fee (${(invoice.card_fee_rate * 100).toFixed(2)}%)`} value={invoice.card_fee} />
            )}
            <div className="flex items-center justify-between pt-2.5 mt-2.5 border-t border-cream/15">
              <div>
                <div className="display-retro text-cream uppercase tracking-widest text-sm">Total owed</div>
                <div className="text-cream/40 text-[10px] uppercase tracking-widest mt-0.5">{invoice.payment_terms || "Due on receipt"}</div>
              </div>
              <span className="display-retro text-orange text-3xl num">${Number(invoice.total).toFixed(2)}</span>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="px-6 pb-4">
              <div className="bg-navy text-cream/70 text-xs rounded-md p-3 border border-cream/10">
                {invoice.notes}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="px-6 pb-6">
            {paid ? (
              <button
                onClick={() => setShowInvoice(true)}
                data-testid="pay-view-invoice"
                className="w-full h-14 bg-cream/10 hover:bg-cream/20 text-cream font-semibold label-retro uppercase tracking-widest text-sm rounded-md transition flex items-center justify-center gap-2 border border-cream/20"
              >
                <FileText className="w-4 h-4" /> View receipt
              </button>
            ) : (
              <button
                onClick={pay}
                disabled={paying}
                data-testid="pay-now-btn"
                className="w-full h-14 bg-orange hover:bg-orange/90 text-cream font-semibold label-retro uppercase tracking-widest text-sm rounded-md transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {paying ? "Connecting…" : (<><Lock className="w-4 h-4" /> Pay ${Number(invoice.total).toFixed(2)} securely <ArrowRight className="w-4 h-4" /></>)}
              </button>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowInvoice(true)}
                data-testid="pay-view-pdf"
                className="h-10 bg-transparent border border-cream/20 hover:border-orange text-cream/80 hover:text-orange text-xs uppercase tracking-widest rounded-md transition inline-flex items-center justify-center gap-1.5"
              >
                <Printer className="w-3.5 h-3.5" /> Print / PDF
              </button>
              <a
                href="tel:+12108001710"
                className="h-10 bg-transparent border border-cream/20 hover:border-orange text-cream/80 hover:text-orange text-xs uppercase tracking-widest rounded-md transition inline-flex items-center justify-center gap-1.5"
                data-testid="pay-call-btn"
              >
                <Phone className="w-3.5 h-3.5" /> Pay by phone
              </a>
            </div>
          </div>
        </article>

        {/* Trust strip */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-cream/50 text-[11px] uppercase tracking-widest">
          <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Stripe-encrypted</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> No card data stored</span>
        </div>
      </div>
    </Shell>
  );
}

// ============== Shell — retro-futuristic background ==============
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-navy text-cream relative overflow-hidden">
      {/* Subtle starfield */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(230,92,0,0.18) 0px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(244,234,213,0.08) 0px, transparent 1px), radial-gradient(circle at 50% 80%, rgba(244,234,213,0.05) 0px, transparent 1px)",
          backgroundSize: "120px 120px, 200px 200px, 80px 80px",
        }}
      />
      <header className="relative border-b border-cream/10">
        <div className="max-w-md mx-auto px-5 py-4">
          <Link to="/" className="inline-block">
            <div className="label-retro text-[10px] uppercase tracking-[0.3em] text-orange">Mission control</div>
            <div className="display-retro text-cream text-lg uppercase tracking-tight">Space Appliance Repair</div>
          </Link>
        </div>
      </header>
      <main className="relative px-5 pt-8">{children}</main>
    </div>
  );
}

function Row({ label, value, tone = "default" }) {
  const tones = { default: "text-cream", emerald: "text-emerald-300" };
  return (
    <div className="flex items-center justify-between">
      <span className="text-cream/60">{label}</span>
      <span className={`num ${tones[tone]}`}>
        {Number(value) < 0 ? "−$" : "$"}{Math.abs(Number(value)).toFixed(2)}
      </span>
    </div>
  );
}

function PaidBanner() {
  return (
    <div className="mb-5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 rounded-lg p-4 flex items-center gap-3">
      <CheckCircle2 className="w-5 h-5 shrink-0" />
      <div>
        <div className="font-semibold">Payment confirmed — thank you!</div>
        <div className="text-xs text-emerald-200/80 mt-0.5">We'll text & email your receipt shortly.</div>
      </div>
    </div>
  );
}

function CancelledBanner() {
  return (
    <div className="mb-5 bg-orange/10 border border-orange/40 text-orange rounded-lg p-4">
      <div className="font-semibold">Payment cancelled</div>
      <div className="text-xs text-orange/80 mt-0.5">No charge was made. You can try again below or pay by phone.</div>
    </div>
  );
}
